import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';

import type { checkoutItemBuyerQuery$data } from '../queries/__generated__/checkoutItemBuyerQuery.graphql';

type Viewer = NonNullable<NonNullable<checkoutItemBuyerQuery$data>['viewer']>;
export type Item = Viewer['item'];
export type Buyer = Viewer['user'];
export type PreCheckoutTransaction = NonNullable<
    ArrayElement<NonNullable<Viewer['preCheckoutTransaction']>['edges']>
>['node'];

function canMakePurchase(
    item: Item,
    buyer: Buyer,
    preCheckoutTransaction: PreCheckoutTransaction
): boolean {
    if (!item) {
        return false;
    }
    const {
        isSold,
        isOnHold,
        isDeleted,
        isNotListed,
        isPriceUponRequest,
        isAuctionEnabled,
        relevantAuctionLot,
        relevantAuctionSolution,
        hasNetPrice,
        seller,
    } = item;
    const buyerId = buyer?.serviceId;
    const isTradeBuyer = buyer?.isTrade;
    const isPrivateOffer = preCheckoutTransaction?.isPrivateOffer;
    const isCounterOffer = preCheckoutTransaction?.isMostRecentlyDealerCountered;
    const isAcceptedOffer = preCheckoutTransaction?.mostRecentBuyerOffer?.isAccepted;
    const isSecondChanceOfferActive = preCheckoutTransaction?.isSecondChanceOfferActive;
    const canBuyerAcceptPrivateOffer = preCheckoutTransaction?.canBuyerAcceptPrivateOffer;
    const { isSuspended, isOpopSuspended, isPending, isClosed } = seller || {};
    const allowedOpopExceptions =
        (isOpopSuspended && (isCounterOffer || isPrivateOffer || isAcceptedOffer)) || !isPending;

    const { isOpen: isAuctionOpen, buyItNowPrice } = relevantAuctionLot || {};
    const { bids: auctionBids, highestBidderId } = relevantAuctionSolution || {};

    let canCheckoutAuction = true;
    if (isAuctionEnabled) {
        if (isAuctionOpen) {
            // auction open (buy it now case) -- no bids & has BIN price
            const totalAuctionBids = auctionBids?.totalResults;
            canCheckoutAuction = totalAuctionBids === 0 && !!buyItNowPrice;
        } else {
            // auction closed -- only the winning bidder or a bidder with a second chance offer
            // may check out
            const userWonAuction = highestBidderId === buyerId;
            canCheckoutAuction = !!(userWonAuction || isSecondChanceOfferActive);
        }
    }

    return !!(
        canCheckoutAuction &&
        allowedOpopExceptions &&
        !isSuspended &&
        !isClosed &&
        !isDeleted &&
        !isSold &&
        !isNotListed &&
        (!isOnHold || canBuyerAcceptPrivateOffer) &&
        (!isPriceUponRequest || canBuyerAcceptPrivateOffer || (isTradeBuyer && hasNetPrice))
    );
}

export default canMakePurchase;
